/**
 * ConfigChart_V2 - Configuration Device Chart (ILP/AHP)
 *
 * Displays configuration parameters for heat pump devices over time:
 * - Outdoor temperature
 * - Target temperature
 * - Calibration value
 * - Fan speed/level
 * - Dual Y-axis (outdoor temp left, config values right)
 */

import React, { useMemo, useRef } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { InfoBox } from '../../Components';
import { formatCelcius } from '../ChartUtils';
import {
  extractTimeseries,
  extractDeviceData,
  getHourlyTicks,
  formatHourLabel
} from './heatingChartUtils';
import { t } from '../../i18n';

const ConfigChart_V2 = ({ data, thermostatId, metadata }) => {
  console.log('ConfigChart_V2: Rendering config device', thermostatId);

  const chartRef = useRef(null);

  // Extract timeseries and device data
  const timeseries = useMemo(() => extractTimeseries(data), [data]);
  const deviceData = useMemo(() =>
    extractDeviceData(timeseries, thermostatId),
    [timeseries, thermostatId]
  );

  // Device details from metadata
  const deviceDetails = metadata.thermostat_details?.[thermostatId];

  // Process chart data - extract config values from device snapshots
  // extractDeviceData already extracts calibration, fan_level, target_temp, outdoor_temp
  // directly from the device data, so we use them from the snapshot level
  const chartData = useMemo(() => {
    return deviceData.map(snapshot => ({
      timestamp: snapshot.timestamp,
      outdoorTemp: snapshot.outdoor_temp,
      targetTemp: snapshot.target_temp,
      calibration: snapshot.calibration,
      fanSpeed: snapshot.fan_level
    }));
  }, [deviceData]);

  // Get hourly ticks
  const hourlyTicks = useMemo(() => getHourlyTicks(chartData), [chartData]);

  // Early return after all hooks
  if (!deviceDetails) {
    console.warn('No config device details found for', thermostatId);
    return null;
  }

  // InfoBox data - show latest configuration
  const configInfo = [
    { label: t('heatingChart.ahpTargetTemp'), value: formatCelcius(deviceDetails.target_temp) },
    { label: t('heatingChart.calibrationValue'), value: formatCelcius(deviceDetails.calibration) },
    { label: t('heatingChart.fanSpeed'), value: deviceDetails.fan_level_txt || `${deviceDetails.fan_level}` },
    { label: t('heatingChart.outdoorTemp'), value: formatCelcius(deviceDetails.outdoor_temp) },
    { label: t('heatingChart.settingsAt'), value: deviceDetails.timestamp || 'N/A' }
  ];

  // Validation
  if (chartData.length === 0) {
    console.warn('ConfigChart_V2: No chart data for config device', thermostatId);
    return (
      <div>
        <InfoBox title={deviceDetails.name} dataPoints={configInfo} />
        <p style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
          No configuration data available for this device
        </p>
      </div>
    );
  }

  return (
    <div>
      <InfoBox title={deviceDetails.name} dataPoints={configInfo} />

      <div ref={chartRef}>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart
            data={chartData}
            margin={{ top: 20, right: 30, bottom: 20, left: 20 }}
          >
            <CartesianGrid stroke="#f5f5f5" />

            <XAxis
              dataKey="timestamp"
              tickFormatter={formatHourLabel}
              ticks={hourlyTicks}
              interval={0}
              tick={{ fontSize: 12 }}
            />

            <YAxis
              yAxisId="left"
              orientation="left"
              label={{ value: t('heatingChart.outdoorTemp') + ' (°C)', angle: -90, position: 'insideLeft' }}
              domain={['auto', 'auto']}
              stroke="#413ea0"
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              label={{ value: t('heatingChart.ahpSettings'), angle: 90, position: 'insideRight' }}
              domain={['auto', 'auto']}
              stroke="#8884d8"
            />

            <Tooltip
              formatter={(value, name) => {
                if (name.includes('Temp') || name.includes('temp')) {
                  return [formatCelcius(value), name];
                }
                return [value, name];
              }}
            />

            <Legend />

            {/* Outdoor Temperature Line */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="outdoorTemp"
              name={t('heatingChart.outdoorTemp')}
              stroke="#8884d8"
              strokeWidth={2}
              dot={false}
            />

            {/* Configuration Lines */}
            <Line
              yAxisId="right"
              type="stepAfter"
              dataKey="targetTemp"
              name={t('heatingChart.ahpTargetTemp')}
              stroke="#82ca9d"
              strokeWidth={2}
              dot={false}
            />

            <Line
              yAxisId="right"
              type="stepAfter"
              dataKey="calibration"
              name={t('heatingChart.ahpCalibration')}
              stroke="#ffc658"
              strokeWidth={2}
              dot={false}
            />

            <Line
              yAxisId="right"
              type="stepAfter"
              dataKey="fanSpeed"
              name={t('heatingChart.ahpFanSpeed')}
              stroke="#ff7300"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p></p>
    </div>
  );
};

export default ConfigChart_V2;
